import React, { ReactText, useCallback, useEffect, useRef } from "react";
import FleetExpensesStore, {
  FleetExpensesGridFilter,
} from "../stores/FleetExpensesStore";
import SettingsStore from "../stores/SettingsStore";
import UserStore from "../stores/UserStore";
import { inject, observer } from "mobx-react";
import { DateRangeDate, IObjectMap } from "../interfaces/common";
import updateSortingAndFiltersOfColumns from "../helpers/filters";
import COLUMNS from "../components/tables/fleetExpenses/config";
import {
  FLEET_EXPENSES_ACTIVE_COLUMNS,
  FLEET_EXPENSES_ORDER_COLUMNS,
} from "../constants/settings";
import { Alert, Form } from "antd";
import DateRange from "../components/declarants/dateRange";
import Search from "../components/declarants/search";
import CollapseHeader from "../components/CollapseHeader";
import Card from "../components/Card/Card";
import TableWrapper from "../components/declarants/tableWrapper";
import Table from "../components/common/Table";
import { ColumnsType } from "antd/lib/table/interface";
import { Logistic } from "../models/Logistic";
import {
  fleetExpensePaymentTypeMap,
  FleetExpensesModelAny,
  PaymentType,
} from "../models/FleetExpenses/FleetExpensesModel";
import Settings from "../components/declarants/Settings";
import AddFleetExpensesTransport from "../components/fleetExpenses/AddFleetExpensesTransport";
import AddFleetExpense from "../components/fleetExpenses/AddFleetExpense";
import SelectFilter from "../components/Grid/headers/SelectFilter";
import { useFleetExpensesTransports } from "../hooks/fleetExpenses/useFleetExpensesTransports";
import { useFleetExpensesSpendingTypes } from "../hooks/fleetExpenses/useFleetExpensesSpendingTypes";
import useFleetExpensesDrivers from "../hooks/fleetExpenses/useFleetExpensesDrivers";
import { currencyMap, CurrencyType } from "../constants/currency";
import { Nullable } from "../models/Common";

const FleetExpensesPage: React.FC<{
  settingsStore: SettingsStore;
  userStore: UserStore;
}> = ({ settingsStore, userStore }) => {
  const [form] = Form.useForm();
  const clients = settingsStore.clients;
  const firstLoad = useRef<boolean>(true);
  const {
    transports,
    isLoading: transportsLoading,
  } = useFleetExpensesTransports(FleetExpensesStore);
  const {
    spendingTypes,
    isLoading: spendingTypesLoading,
  } = useFleetExpensesSpendingTypes(FleetExpensesStore);
  const {
    drivers,
    isLoading: driversLoading,
    error: driversError,
  } = useFleetExpensesDrivers(FleetExpensesStore);

  useEffect(() => {
    FleetExpensesStore.init();
    if (!firstLoad.current) return;

    FleetExpensesStore.load({}, true);
    firstLoad.current = false;
  }, []);

  const onLoadHandler = useCallback(
    (parameters?: IObjectMap, forceUpdate?: boolean): void => {
      FleetExpensesStore.load(
        {
          ...parameters,
          searchPhrase: FleetExpensesStore.parameters.searchPhrase,
          start_date: FleetExpensesStore.parameters.start_date,
          end_date: FleetExpensesStore.parameters.end_date,
        },
        forceUpdate
      );
    },
    []
  );

  const applyFilter = (
    key: FleetExpensesGridFilter,
    value: Nullable<string>
  ): void => {
    FleetExpensesStore.load(
      {
        page: 1,
        [key]: value?.toString() || "",
      },
      true
    );
  };

  const transportFilter = (value: string): void => {
    applyFilter("tc_id", value);
    FleetExpensesStore.setFilter("tc_id", value);
  };

  const spendingTypeFilter = (value: string): void => {
    applyFilter("spending_type_id", value);
    FleetExpensesStore.setFilter("spending_type_id", value);
  };

  const currencyFilter = (value: CurrencyType): void => {
    applyFilter("currency", value.toUpperCase());
    FleetExpensesStore.setFilter("currency", value);
  };

  const paymentTypeFilter = (value: PaymentType): void => {
    applyFilter("payment_type", value);
    FleetExpensesStore.setFilter("payment_type", value);
  };

  const driverFilter = (value: string): void => {
    applyFilter("driver_id", value);
    FleetExpensesStore.setFilter("driver_id", value || null);
  };

  useEffect(() => {
    settingsStore?.setColumns(
      updateSortingAndFiltersOfColumns<FleetExpensesModelAny>(
        COLUMNS(),
        FleetExpensesStore.parameters
      ),
      FLEET_EXPENSES_ORDER_COLUMNS,
      FLEET_EXPENSES_ACTIVE_COLUMNS
    );
  }, [
    settingsStore,
    settingsStore.settings,
    userStore,
    userStore.user,
    settingsStore.isEnabledReorder,
    FleetExpensesStore.parameters,
    clients,
  ]);

  return (
    <>
      <CollapseHeader yGap={12}>
        <DateRange
          store={FleetExpensesStore}
          startDate={FleetExpensesStore.parameters.start_date as DateRangeDate}
          endDate={FleetExpensesStore.parameters.end_date as DateRangeDate}
          allowClear={true}
        />
        {drivers && !driversLoading && (
          <SelectFilter
            items={[
              { title: "All drivers", value: "" },
              ...drivers.getOptions(),
            ]}
            onChange={driverFilter}
            placeholder="Driver"
            defaultValue={
              FleetExpensesStore.getFilter("driver_id") || undefined
            }
          />
        )}
        {transports && !transportsLoading && (
          <SelectFilter
            items={transports.getOptions()}
            onChange={transportFilter}
            placeholder={"TC"}
            defaultValue={FleetExpensesStore.getFilter("tc_id") || undefined}
          />
        )}
        {spendingTypes && !spendingTypesLoading && (
          <SelectFilter
            items={spendingTypes.getOptions()}
            onChange={spendingTypeFilter}
            placeholder={"Spending Type"}
            defaultValue={
              FleetExpensesStore.getFilter("spending_type_id") || undefined
            }
          />
        )}
        <SelectFilter
          items={currencyMap.getOptions()}
          onChange={currencyFilter}
          placeholder={"Currency"}
          defaultValue={FleetExpensesStore.getFilter("currency") || undefined}
        />
        <SelectFilter
          items={fleetExpensePaymentTypeMap.getOptions()}
          onChange={paymentTypeFilter}
          placeholder={"Payment Type"}
          defaultValue={
            FleetExpensesStore.getFilter("payment_type") || undefined
          }
        />
        <Search store={FleetExpensesStore} />
        <div />
      </CollapseHeader>
      {driversError && (
        <Alert
          message={driversError}
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <Card>
        <Card.Header>
          <AddFleetExpensesTransport />
          <AddFleetExpense />
          <Settings reorderable />
        </Card.Header>
        <TableWrapper
          enableScrollBar={settingsStore.isEnabledScrollbar}
          className="fleet-expenses-table"
        >
          <Form form={form} component={false}>
            <Table
              key="loading-done"
              columns={settingsStore.activeColumns as ColumnsType<Logistic>}
              settingsStore={settingsStore}
              data={FleetExpensesStore.data}
              onLoad={onLoadHandler}
              isLoading={FleetExpensesStore.isLoadingTableData}
              onRowSelectors={(items: ReactText[]) => {
                FleetExpensesStore.changeSelectedRows(items);
              }}
              checkStrictly={false}
              selectedRows={FleetExpensesStore.selectedRows}
              expandableConfig={{
                expandRowByClick: false,
                indentSize: 10,
              }}
              onExpandedRowsChange={(expandedRows) => {
                FleetExpensesStore.expandedRows = expandedRows;
              }}
              expandedRows={FleetExpensesStore.expandedRows}
              hideSelectAll
            />
          </Form>
        </TableWrapper>
      </Card>
    </>
  );
};

export default inject(({ store }) => ({
  settingsStore: store.settingsStore,
  userStore: store.userStore,
}))(observer(FleetExpensesPage));
